/*
  SnapReserve reviews
  - Reviews are edited in the theme editor ("Customer reviews" section group).
  - This script shows them inside the SnapReserve page: all reviews newest
    first, the star rating under the headline, and the reviews ticked
    "Show under the buy button" (up to 3, newest first).
  - The rating, review count and star breakdown are worked out from the reviews.
*/
(() => {
  const FIRST_DESKTOP = 6;
  const FIRST_MOBILE = 4;
  const BATCH = 8;
  const MAX_HIGHLIGHTS = 3;
  const EDITOR_ATTR = 'data-shopify-editor-block';
  const mobile = window.matchMedia('(max-width: 640px)');

  const plural = (n) => (n === 1 ? '' : 's');
  const newestFirst = (a, b) => {
    const ka = a.dataset.sort || '';
    const kb = b.dataset.sort || '';
    return ka < kb ? 1 : ka > kb ? -1 : 0;
  };

  // Reviews from every "Customer reviews" section, newest first.
  function collect(selector) {
    const items = [];
    document.querySelectorAll('[data-sr-rv-source]').forEach((source) => {
      source.querySelectorAll(selector).forEach((el) => items.push(el));
    });
    return items.sort(newestFirst);
  }

  // Show a copy of the review; in the theme editor the visible copy keeps the
  // block link so clicking a review in the sidebar highlights the right card.
  function displayCopy(original) {
    const copy = original.cloneNode(true);
    const editorData = original.getAttribute(EDITOR_ATTR) || original.dataset.rvEditorBlock;
    if (editorData) {
      original.dataset.rvEditorBlock = editorData;
      original.removeAttribute(EDITOR_ATTR);
      copy.setAttribute(EDITOR_ATTR, editorData);
      copy.removeAttribute('data-rv-editor-block');
    }
    return copy;
  }

  function stats(cards) {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;
    let rated = 0;
    cards.forEach((card) => {
      const r = parseInt(card.dataset.rating, 10);
      if (r >= 1 && r <= 5) {
        counts[r] += 1;
        sum += r;
        rated += 1;
      }
    });
    const avg = rated ? Math.round((sum / rated) * 10) / 10 : 0;
    const positive = rated ? Math.round(((counts[4] + counts[5]) * 100) / rated) : 0;
    return { count: cards.length, rated, avg, counts, positive };
  }

  function setStars(stars, rating, label) {
    if (!stars) return;
    stars.style.setProperty('--sr-stars-fill', `${Math.max(0, Math.min(5, rating)) * 20}%`);
    stars.setAttribute('aria-label', label);
  }

  /* ---------- Full reviews section ---------- */
  const shellCards = (shell) => Array.from(shell.querySelectorAll('[data-rv-grid] > [data-rv-card]'));
  const isCollapsed = (card) =>
    card.classList.contains('is-collapsed') || (mobile.matches && card.classList.contains('is-collapsed-sm'));

  function updateMore(shell) {
    const more = shell.querySelector('[data-rv-more]');
    const grid = shell.querySelector('[data-rv-grid]');
    if (!more || !grid) return;
    const left = shellCards(shell).filter(isCollapsed).length;
    more.hidden = left === 0 || Boolean(grid.dataset.filter);
    const count = more.querySelector('[data-rv-more-count]');
    if (count) count.textContent = left;
  }

  function setFilter(shell, rating) {
    const grid = shell.querySelector('[data-rv-grid]');
    const status = shell.querySelector('[data-rv-status]');
    const statusText = shell.querySelector('[data-rv-status-text]');
    const cards = shellCards(shell);
    if (rating) {
      grid.dataset.filter = rating;
      let n = 0;
      cards.forEach((card) => {
        const match = card.dataset.rating === rating;
        card.classList.toggle('is-filtered', !match);
        if (match) n += 1;
      });
      if (statusText) statusText.textContent = `Showing ${n} ${rating}-star review${plural(n)}`;
      if (status) status.hidden = false;
    } else {
      delete grid.dataset.filter;
      cards.forEach((card) => card.classList.remove('is-filtered'));
      if (status) status.hidden = true;
    }
    shell.querySelectorAll('[data-rv-filter]').forEach((bar) => {
      bar.setAttribute('aria-pressed', String(bar.dataset.rvFilter === rating));
    });
    updateMore(shell);
  }

  function bindShell(shell) {
    if (shell.dataset.rvBound === 'true') return;
    shell.dataset.rvBound = 'true';
    const more = shell.querySelector('[data-rv-more]');
    if (more) {
      more.addEventListener('click', () => {
        let shown = 0;
        let first = null;
        for (const card of shellCards(shell)) {
          if (shown >= BATCH) break;
          if (isCollapsed(card)) {
            card.classList.remove('is-collapsed', 'is-collapsed-sm');
            if (!first) first = card;
            shown += 1;
          }
        }
        updateMore(shell);
        if (first) first.focus({ preventScroll: true });
      });
    }
    shell.querySelectorAll('[data-rv-filter]').forEach((bar) => {
      bar.addEventListener('click', () => {
        const grid = shell.querySelector('[data-rv-grid]');
        const rating = bar.dataset.rvFilter;
        setFilter(shell, grid.dataset.filter === rating ? '' : rating);
      });
    });
    const clear = shell.querySelector('[data-rv-clear]');
    if (clear) clear.addEventListener('click', () => setFilter(shell, ''));
  }

  function renderShell(shell, cards, s) {
    const grid = shell.querySelector('[data-rv-grid]');
    if (!grid) return;
    if (!cards.length) {
      grid.replaceChildren();
      shell.hidden = true;
      return;
    }
    const copies = cards.map(displayCopy);
    copies.forEach((card, i) => {
      card.classList.remove('is-collapsed', 'is-collapsed-sm', 'is-filtered');
      if (i >= FIRST_DESKTOP) card.classList.add('is-collapsed');
      else if (i >= FIRST_MOBILE) card.classList.add('is-collapsed-sm');
    });
    grid.replaceChildren(...copies);

    const summary = shell.querySelector('[data-rv-summary]');
    if (summary) summary.hidden = s.rated === 0;
    const avgText = s.avg.toFixed(1);
    shell.querySelectorAll('[data-rv-avg]').forEach((el) => { el.textContent = avgText; });
    setStars(shell.querySelector('.sr-rv__score .sr-stars'), s.avg, `Average rating ${avgText} out of 5`);
    const basedOn = shell.querySelector('[data-rv-based-on]');
    if (basedOn) basedOn.textContent = `Based on ${s.count} review${plural(s.count)}`;
    shell.querySelectorAll('[data-rv-filter]').forEach((bar) => {
      const star = Number(bar.dataset.rvFilter);
      const n = s.counts[star] || 0;
      const fill = bar.querySelector('[data-rv-bar-fill]');
      if (fill) fill.style.width = `${s.rated ? (n * 100) / s.rated : 0}%`;
      const count = bar.querySelector('[data-rv-bar-count]');
      if (count) count.textContent = n;
      bar.disabled = n === 0;
      bar.setAttribute('aria-label', `Show ${star}-star reviews (${n})`);
    });
    const positive = shell.querySelector('[data-rv-positive]');
    if (positive) positive.textContent = `${s.positive}%`;

    shell.hidden = false;
    bindShell(shell);
    setFilter(shell, '');
  }

  /* ---------- Put everything on the page ---------- */
  function render() {
    const cards = collect('[data-rv-card]');
    const highlights = collect('[data-rv-compact]').slice(0, MAX_HIGHLIGHTS);
    const s = stats(cards);
    const hasShell = Boolean(document.querySelector('[data-sr-rv-shell]'));

    document.querySelectorAll('[data-sr-rv-rating]').forEach((el) => {
      el.classList.remove('is-pending');
      if (!s.rated) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      if (!hasShell) el.removeAttribute('href');
      const avgText = s.avg.toFixed(1);
      setStars(el.querySelector('.sr-stars'), s.avg, `Rated ${avgText} out of 5 from ${s.count} review${plural(s.count)}`);
      const avg = el.querySelector('[data-rv-avg]');
      if (avg) avg.textContent = avgText;
      const count = el.querySelector('[data-rv-count-text]');
      if (count) count.textContent = `${s.count} customer review${plural(s.count)}`;
    });

    document.querySelectorAll('[data-sr-rv-hl]').forEach((el) => {
      const track = el.querySelector('[data-rv-hl-track]');
      if (!track) return;
      track.replaceChildren(...highlights.map((h) => h.cloneNode(true)));
      track.classList.toggle('is-single', highlights.length === 1);
      el.querySelectorAll('[data-rv-count]').forEach((n) => { n.textContent = s.count; });
      el.hidden = highlights.length === 0;
    });

    document.querySelectorAll('[data-sr-rv-shell]').forEach((shell) => renderShell(shell, cards, s));
  }

  let queued = null;
  const renderSoon = () => {
    clearTimeout(queued);
    queued = setTimeout(render, 30);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }
  if (mobile.addEventListener) {
    mobile.addEventListener('change', () => document.querySelectorAll('[data-sr-rv-shell]').forEach(updateMore));
  }

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:load', renderSoon);
  document.addEventListener('shopify:section:unload', renderSoon);
  document.addEventListener('shopify:section:reorder', renderSoon);
  document.addEventListener('shopify:section:select', (event) => {
    if (!event.target || !event.target.querySelector || !event.target.querySelector('[data-sr-rv-source]')) return;
    const shell = document.querySelector('[data-sr-rv-shell]:not([hidden])');
    if (shell) shell.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const id = event.detail && event.detail.blockId;
    if (!id) return;
    const card = document.querySelector(`[data-sr-rv-shell] [data-rv-block-id="${CSS.escape(id)}"]`);
    if (!card) return;
    const shell = card.closest('[data-sr-rv-shell]');
    if (card.classList.contains('is-filtered')) setFilter(shell, '');
    card.classList.remove('is-collapsed', 'is-collapsed-sm');
    updateMore(shell);
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();
